import React from "react";

import { Button, Form, Modal, Space, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { ActionSchema, ItemModificationSchema, ItemSchema, MenuType } from "../../types/model";
import { ItemSchemaForm } from "./ItemSchemaForm";
import { useAntdTheme } from "../../shared/hooks";
import { useModal } from "../../shared/hooks/useModal";
import { zIndex } from "../../config/theme";

const { Title } = Typography;

type ItemSchemaModalProps = {
  restaurantId: string;
  item_schema: ItemSchema;
  menu_type: MenuType;

  // True if this is a new schema being added instead of updating an existing
  // one.
  is_new: boolean;

  // All action schemas, not just ones already assigned to this item schema.
  action_schemas: ActionSchema[];

  // All modification schemas, not just ones already assigned to this item
  // schema.
  item_modification_schemas: ItemModificationSchema[];

  on_update_success: (new_schema: ItemSchema) => void;
};

export const ItemSchemaModal = (props: ItemSchemaModalProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const modal = useModal();
  const [form] = Form.useForm();
  const [saveLoading, setSaveLoading] = React.useState(false);

  return (
    <div>
      <Modal
        open={modal.isOpen}
        title={
          <Title level={4} style={{ margin: 0 }}>
            {props.is_new ? t("create_item") : props.item_schema.name}
          </Title>
        }
        onCancel={modal.close}
        destroyOnClose={true}
        zIndex={zIndex.modal}
        width="calc(100vw - 48px)"
        style={{ top: 24 }}
        styles={{
          body: { maxHeight: "calc(100vh - 160px)", overflowY: "auto", padding: token.paddingXL },
        }}
        footer={
          <Space>
            <Button onClick={modal.close}>{t("cancel")}</Button>
            <Button type="primary" loading={saveLoading} onClick={() => form.submit()}>
              {t("save")}
            </Button>
          </Space>
        }
      >
        <ItemSchemaForm
          {...props}
          form={form}
          saveLoading={saveLoading}
          setSaveLoading={setSaveLoading}
          on_update_success={(new_schema: ItemSchema) => {
            modal.close();
            props.on_update_success(new_schema);
          }}
        />
      </Modal>
      {props.is_new ? (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={modal.open}
          style={{
            float: "right",
            borderRadius: token.borderRadiusLG,
            height: token.controlHeight,
          }}
        >
          {t("create_item")}
        </Button>
      ) : (
        <Button type="link" style={{ padding: 0 }} onClick={modal.open}>
          {props.item_schema.name}
        </Button>
      )}
    </div>
  );
};
